import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import cryptoJs from "crypto-js";
import securekey from "../../../config";
import { permissionObj } from "../../../Helpers/permission";
import routes from "../../../Routes/routes";
import { IconsMenuCard } from "./IconsMenuCard";
import jwtDecode from "jwt-decode";
import {ROLE_NAME_DEFAULT_MODEL, USER_TYPE_NAME_MODEL} from "../../../enums/userTypeEnum";
import {RoleCheck} from "../../../reduxToolkit/authentication/AuthenticatonApi";
import {STATUS_MODEL} from "../../../enums/statusEnum";
import {GetUserDocumentsEmployee} from "../../../reduxToolkit/EmployeeProviders/EmployeeProvidersApi";

/**
 * @fileoverview LoginOptionsApp hook responsible for preparing user-specific menu configurations,
 * access permissions, UI state (hover and style), and redirects based on user type and status.
 * It also handles role verification and document redirection for users whose documents are pending approval.
 *
 * @module hooks/LoginOptionsApp
 * @author Ferro Zapata Arnulfo
 * @email azapata@ibl.mx
 * @jobTitle Full Stack Developer
 */


/**
 * React hook that handles:
 * - Parsing and decrypting the auth token
 * - Dispatching the role verification API
 * - Redirecting users with "TO_APPROVE_DOCUMENT" status
 * - Building dynamic menu configurations based on permission sets and user types
 * - UI hover and style state for interactive menus
 * - Background preview logic on menu icon hover
 *
 * @returns {{
 *   changeStyle: string,
 *   isHovering: string,
 *   hover: { isMenu: boolean },
 *   user: object,
 *   DEV: any,
 *   permission: object,
 *   setHover: (value: { isMenu: boolean }) => void,
 *   menuItemsEmployee: Array<object>,
 *   menuItemsSupplier: Array<object>,
 *   menuItemsContractor: Array<object>
 * }} An object containing current UI state, user info, permission info, and menu items for different user types.
 */

export const LoginOptionsApp = () => {
    const [changeStyle, setChangeStyle] = useState('company');
    const [isHovering, setIsHovering] = useState('1');
    const [hover, setHover] = useState({ isMenu: false });
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const Etoken = sessionStorage.getItem('bearerToken') || '';
    const bytes = cryptoJs.AES.decrypt(Etoken, securekey);
    const token = bytes.toString(cryptoJs.enc.Utf8);
    const { user, permission, getUserByIdAtEntry} = useSelector((state) => state.authenticatioauthennSlice);
    const roleName = getUserByIdAtEntry.roleName;
    const [renderCount, setRenderCount] = useState(0);

    const menuItemsEmployee = [
        {
            permissionKey: permissionObj?.WEB_COMPANY_READ,
            navigateTo: routes.company.list,
            icon: IconsMenuCard.icCompany,
            label: t('company_profile'),
            menuName: 'COMPANY',
        },
        {
            permissionKey: permissionObj?.WEB_ZONE_READ,
            navigateTo: routes.zone.list,
            icon: IconsMenuCard.icFirstAccess,
            label: t('first_access'),
            menuName: 'ZONES',
        },
        {
            permissionKey: permissionObj?.WEB_DEVICE_READ,
            navigateTo: routes.devices.list,
            icon: IconsMenuCard.icDevices,
            label: t('devices'),
            menuName: 'device setting',
        },
        {
            permissionKey: [permissionObj?.WEB_ATTENDANCE_READ,
                permissionObj?.WEB_ATTENDANCE_SHIFT_READ,
                permissionObj?.WEB_ATTENDANCE_SETTING_READ,
                permissionObj?.WEB_ATTENDANCE_DEPARTMENT_POLICIES_READ
            ],
            navigateTo: routes.attendance.list,
            icon: IconsMenuCard.icAttendance,
            label: t('attendance'),
            menuName: 'PAYROLL',
        },
        {
            permissionKey: permissionObj?.WEB_DEPARTMENT_READ,
            navigateTo: routes.department.list,
            icon: IconsMenuCard.icDepartments,
            label: t('departments'),
            menuName: 'DEPARTMENTS',
        },
        {
            permissionKey: permissionObj?.WEB_EMPLOYEE_READ,
            navigateTo: routes.employee.list,
            icon: IconsMenuCard.icEmployees,
            label: t('employees'),
            menuName: 'EMPLOYEES',
        },
        {
            permissionKey: permissionObj?.WEB_VEHICLE_READ,
            navigateTo: routes.vehicleFleet.list,
            icon: IconsMenuCard.icVehicleFleet,
            label: t('vehicle_fleet'),
            menuName: 'VEHICLES',
        },
        {
            permissionKey: [
                permissionObj?.WEB_EVENT_READ,
                permissionObj?.WEB_EVENT_STATUS_READ,
                permissionObj?.WEB_EVENT_MANAGE_READ
            ],
            navigateTo: routes.event.list,
            icon: IconsMenuCard.icEvents,
            label: t('reservations'),
            menuName: 'RESERVATIONS',
        },
        {
            permissionKey: permissionObj?.WEB_HID_READ,
            navigateTo: routes.controller.list,
            icon: IconsMenuCard.icController,
            label: t('controller'),
            menuName: 'CONTROLLER',
        },
        {
            permissionKey: permissionObj?.WEB_WORK_SHIFT_READ,
            navigateTo: routes.workshift.list,
            icon: IconsMenuCard.icWorkshipPanel,
            label: t('work_shift_panel'),
            menuName: 'WORK SHIFT PANEL',
        },
        {
            permissionKey: permissionObj?.WEB_ACCESS_USER_READ,
            navigateTo: routes.access.list,
            icon: IconsMenuCard.icAccess,
            label: t('access'),
            menuName: 'access',
        },
        {
            permissionKey: permissionObj?.WEB_PRIVILEGE_READ,
            navigateTo: routes.roles.list,
            icon: IconsMenuCard.icPrivilageSettings,
            label: t('privilage_settings'),
            menuName: 'privilage settings',
        },
        {
            permissionKey: permissionObj?.WEB_ONBOARDING_READ,
            navigateTo: routes.onboarding.list,
            icon: IconsMenuCard.icOnBoarding,
            label: t('on_boarding'),
            menuName: 'onBoarding',
        },
        {
            permissionKey: [
                permissionObj.WEB_CONTRACTOR_CONTRACT_READ,
                permissionObj?.WEB_CONTRACTOR_READ,
                permissionObj?.WEB_CONTRACTOR_MANAGE_READ,
                permissionObj.WEB_CONTRACTOR_APPROVAL_FLOW_READ,
                permissionObj.WEB_CONTRACTOR_REQUEST_READ,
                permissionObj.WEB_CONTRACTOR_REQUEST_MANAGE

            ],
            permissionKeyAlt: permissionObj?.WEB_CONTRACTOR_CONTRACT_READ,
            navigateTo: routes.contractor.list,
            icon: IconsMenuCard.icContractors,
            label: t('contractors'),
            menuName: 'CONTRACTORS',
        },
        {
            permissionKey: [
                permissionObj.WEB_SUPPLIER_ORDER_READ,
                permissionObj?.WEB_SUPPLIER_READ,
                permissionObj?.WEB_SUPPLIER_MANAGE_READ,
                permissionObj.WEB_SUPPLIER_APPROVAL_FLOW_READ,
                permissionObj?.WEB_SUPPLIER_REQUEST_READ,
                permissionObj?.WEB_SUPPLIER_REQUEST_MANAGE
            ],
            permissionKeyAlt: permissionObj?.WEB_SUPPLIER_ORDER_READ,
            navigateTo: routes.suppliers.main,
            icon: IconsMenuCard.icSupplier,
            label: t('suppliers'),
            menuName: 'SUPPLIER',
        },
        roleName === ROLE_NAME_DEFAULT_MODEL.ROLE_GUARD &&
        {
            permissionKey: permissionObj?.WEB_GUARD_READ,
            navigateTo: routes.guard.list,
            icon: IconsMenuCard.icGuard,
            label: t('guard'),
            menuName: 'GUARD',
        },
        {
            permissionKey: permissionObj?.WEB_SUPPLIER_DOCUMENT_READ,
            permissionKeyAlt: permissionObj?.WEB_EMPLOYEE_DOCUMENT_READ,
            navigateTo: routes.document.list,
            icon: IconsMenuCard.icDocumentPanel,
            label: t('user_document_management'),
            menuName: 'DOCUMENT_PANEL',
        },
        {
            permissionKey: permissionObj?.WEB_EMAIL_SETTINGS_READ,
            navigateTo: routes.email.list,
            icon: IconsMenuCard.icEmailServiceSettings,
            label: t('email_service_settings'),
            menuName: 'email setting',
        },
        {
            permissionKey: permissionObj?.WEB_BACK_UP_READ,
            navigateTo: routes.backup.list,
            icon: IconsMenuCard.icBackUp,
            label: t('back_up'),
            menuName: 'BACK UP',
        },
        {
            permissionKey: permissionObj?.WEB_HOME_OFFICE_READ,
            navigateTo: routes.homeOffice.list,
            icon: IconsMenuCard.icHomeOffice,
            label: t('home_office'),
            menuName: 'work_office',
        },
        {
            permissionKey: [permissionObj?.WEB_RESOURCES_READ, permissionObj?.WEB_CARD_DESIGNER_READ],
            navigateTo: routes.cardDesigner.list,
            icon: IconsMenuCard.icCardDesigner,
            label: t('card_designer'),
            menuName: 'card_designer',
        },
        {
            permissionKey: permissionObj?.WEB_LOG_PLATFORM_READ,
            permissionKeyAlt: permissionObj?.WEB_LOG_ACTION_READ,
            navigateTo: routes.logPlatform.list,
            icon: IconsMenuCard.icLogs,
            label: t('logs'),
            menuName: 'LOGS',
        },
        {
            permissionKey: permissionObj?.WEB_ANNOUNCEMENT_READ,
            navigateTo: routes.announcement.list,
            icon: IconsMenuCard.icAnnouncements,
            label: t('announcements'),
            menuName: 'announcements',
        },
        {
            permissionKey: permissionObj?.WEB_HARD_DELETE_READ,
            navigateTo: routes.hardDelete.list,
            icon: IconsMenuCard.icDeleteRestore,
            label: t('delete_and_restore'),
            menuName: 'delete',
        },
        {
            permissionKey: permissionObj?.WEB_CHART_READ,
            navigateTo: routes.graphs.list,
            icon: IconsMenuCard.icGraphs,
            label: t('graphs'),
            menuName: 'graphs',
        },
        {
            permissionKey: permissionObj?.WEB_CARD_READ,
            navigateTo: routes.cards.list,
            icon: IconsMenuCard.icCardManagment,
            label: t('card_management'),
            menuName: 'cards',
        },
        {
            permissionKey: [
                permissionObj?.WEB_INGREDIENT_READ,
                permissionObj?.WEB_FOODIE_ITEM_READ,
                permissionObj?.WEB_FOODIE_MENU_READ,
                permissionObj?.WEB_FOODIE_CAFETERIA_READ,
                permissionObj?.WEB_FOODIE_SHIFT_READ,
                permissionObj?.WEB_FOODIE_MEAL_TIME_READ,
                permissionObj?.WEB_FOODIE_ATTENDANCE_READ,
                permissionObj?.WEB_FOODIE_DEPARTMENT_POLICIES_READ,
                permissionObj?.WEB_TOTAL_MEALS_READ,
                permissionObj?.WEB_FOODIE_MEAL_FORECAST_READ,
                permissionObj?.WEB_FOODIE_ACCESS_READ,
                permissionObj?.WEB_FOODIE_REQUEST_READ,
                permissionObj?.WEB_FOODIE_MANAGE_SETTINGS_READ,
            ],
            navigateTo: routes.foodie.list,
            icon: IconsMenuCard.icDiningManagement,
            label: t('dining_management'),
            menuName: 'dining_management',
        },
        {
            permissionKey: [permissionObj?.WEB_FOODIE_MENU_SELECTION_VIEW, permissionObj?.WEB_FOODIE_EXTRA_REQUEST_VIEW],
            navigateTo: routes.foodieOrders.main,
            icon: IconsMenuCard.icCafeteria,
            label: t('foodie_dining'),
            menuName: 'foodie_dining',
        },
        {
            permissionKey: permissionObj?.WEB_PROFILE_READ,
            navigateTo: routes.profile.list,
            icon: IconsMenuCard.icProfile,
            label: t('profile'),
            menuName: 'PROFILE',
        },
        {
            permissionKey: permissionObj?.WEB_SYSTEM_CONFIG_READ,
            navigateTo: routes.systemConfig.list,
            icon: IconsMenuCard.icSystemSettings,
            label: t('system_config'),
            menuName: 'systemConfig',
        },
        {
            permissionKey: permissionObj?.WEB_DISTRIBUTION_CENTER_READ,
            navigateTo: routes.distributionCenter.list,
            icon: IconsMenuCard.icDistributioCenter,
            label: t('distribution_center'),
            menuName: 'distributionCenter',
        },
        {
            permissionKey: permissionObj?.WEB_APPROVAL_FLOW_READ,
            navigateTo: routes.approval.list,
            icon:  IconsMenuCard.icApprovalFlowBlack,
            label: t('APPROVAL_FLOW'),
            menuName: 'APPROVAL',
        }
        , {
            permissionKey: permissionObj?.WEB_SERVICE_TYPE_READ,
            navigateTo: routes.serviceType.list,
            icon: IconsMenuCard.icServiceType,
            label: t('service_type'),
            menuName: 'serviceType',
        },
        {
            permissionKey: permissionObj?.WEB_LOGISTIC_REQUEST_READ,
            navigateTo: routes.transportRequest.list,
            icon: IconsMenuCard.icTransportRequest,
            label: t('transport_request'),
            menuName: 'transportRequest',
        },
        {
            permissionKey: roleName === ROLE_NAME_DEFAULT_MODEL.ROLE_GUARD ?
                [
                    permissionObj?.WEB_VISITOR_READ,
                    permissionObj?.WEB_REQUEST_ACCESS_READ
                ]
                :[
                    permissionObj?.WEB_VISITOR_INVITATION_READ,
                    permissionObj?.WEB_VISITOR_REQUEST_READ,
                    permissionObj?.WEB_VISITOR_READ,
                    permissionObj?.WEB_VISITOR_TYPE_READ,
                    permissionObj?.WEB_REQUEST_ACCESS_READ
                ],
            navigateTo: roleName === ROLE_NAME_DEFAULT_MODEL.ROLE_GUARD ? routes.guard.visitor : routes.visitor.list,
            icon:  IconsMenuCard.icVisitorManagment,
            label: t('visitor_management'),
            menuName: 'visitorManagement',
        },
        {
            permissionKey: [
                permissionObj?.WEB_FINE_TYPE_READ,
                permissionObj?.WEB_TRAFFIC_DASHBOARD_GEOFENCES_READ,
                permissionObj?.WEB_TRAFFIC_DASHBOARD_FINES_READ,
                permissionObj?.WEB_TRAFFIC_USERS_READ,
                permissionObj?.WEB_TRAFFIC_MANAGE_FINES_READ,
                permissionObj?.WEB_TRAFFIC_MANAGE_GEOFENCES_READ
            ],
            navigateTo: routes.traffic.list,
            icon:  IconsMenuCard.icTraffic,
            label: t('traffic_main_system'),
            menuName: 'trafficMainSystem',
        },
        {
            permissionKey: [
                permissionObj?.WEB_KITCHEN_TICKETS_READ, permissionObj?.WEB_KITCHEN_TICKETS_HISTORY_READ
            ],
            navigateTo: routes.foodieKitchen.list,
            icon: IconsMenuCard.icKitchenTickets,
            label: t('kitchen_tickets'),
            menuName: 'kitchenTickets',
        },
        {
            permissionKey: roleName === ROLE_NAME_DEFAULT_MODEL.ROLE_GUARD ? permissionObj?.WEB_VISITOR_INCOMING_INVITATION_READ : false,
            menuName: 'incomingInvitations',
            navigateTo: routes.guard.incomingInvitation,
            label: t('incoming_invitations'),
            icon: IconsMenuCard.icIncomingInvitation,
        },
        {
            permissionKey: roleName === ROLE_NAME_DEFAULT_MODEL.ROLE_GUARD ? permissionObj?.WEB_CONTRACTOR_INCOMING_READ : false,
            menuName: 'incomingContractor',
            navigateTo: routes.guard.incomingContractors,
            label: t('incoming_contractors'),
            icon: IconsMenuCard.icIncomingContractors,
        },
        {
            permissionKey: [
                permissionObj?.WEB_FOODIE_KITCHEN_ACCESS_READ, permissionObj?.WEB_FOODIE_KITCHEN_ACCESS_HISTORY_READ
            ],
            navigateTo: routes.deliveredServices.list,
            icon: IconsMenuCard.icDeliveredServices,
            label: t('delivered_services'),
            menuName: 'deliveredServices',
        },
        {
            permissionKey: [
                permissionObj?.WEB_MATERIAL_REQUEST_READ
            ],
            navigateTo: routes.materialRequest.main,
            icon: IconsMenuCard.icMaterialRequest,
            label: t('material_control'),
            menuName: 'materialRequest',
        },
    ];

    const menuItemsSupplier = [
        {
            navigateTo: routes.suppliers.employees,
            icon: IconsMenuCard.icEmployees,
            label: t('employees'),
            menuName: 'EMPLOYEES',
        },
        {
            navigateTo: routes.vehicleSupplier.list,
            icon: IconsMenuCard.icVehicleFleet,
            label: t('vehicle_fleet'),
            menuName: 'VEHICLES',
        },
        {
            navigateTo: routes.orders.list,
            icon: IconsMenuCard.icSupplier,
            label: t('orders'),
            menuName: 'Orders',
        },
        {
            navigateTo: routes.announcement.supplier,
            icon: IconsMenuCard.icAnnouncements,
            label: t('announcements'),
            menuName: 'announcements',
        },
        {
            navigateTo: routes.profile.supplier,
            icon: IconsMenuCard.icProfile,
            label: t('profile'),
            menuName: 'profile',
        },
        {
            navigateTo: routes.suppliers.transportRequest,
            icon: IconsMenuCard.icTransportRequest,
            label: t('transport_request'),
            menuName: 'transportRequest',
        },
        {
            navigateTo: routes.suppliers.foodie,
            icon: IconsMenuCard.icCafeteria,
            label: t('foodie_dining'),
            menuName: 'foodie_dining',
        },
        {
            navigateTo: routes.suppliers.companyDocumentation,
            icon: IconsMenuCard.icCompanyDocumentation,
            label: t('company_documentation'),
            menuName: 'companyDocumentation',
        }
    ];

    const menuItemsContractor = [
        {
            navigateTo: routes.contractor.searchEmployee,
            icon: IconsMenuCard.icEmployees,
            label: t('employees'),
            menuName: 'employees',
        },
        {
            navigateTo: routes.vehicleContractor.search,
            icon: IconsMenuCard.icVehicleFleet,
            label: t('vehicle_fleet'),
            menuName: 'vehicles',
        },
        {
            navigateTo: routes.contractor.contractList,
            icon: IconsMenuCard.icContractors,
            label: t('contracts'),
            menuName: 'Contracts',
        },
        {
            navigateTo: routes.announcement.contractor,
            icon: IconsMenuCard.icAnnouncements,
            label: t('announcements'),
            menuName: 'announcements',
        },
        {
            navigateTo: routes.profile.contractor,
            icon: IconsMenuCard.icProfile,
            label: t('profile'),
            menuName: 'profile',
        },
        {
            navigateTo: routes.accessRights.list,
            icon: IconsMenuCard.icAccess,
            label: t('access_rights'),
            menuName: 'access-rights',
        },
        {
            navigateTo: routes.contractor.foodie,
            icon: IconsMenuCard.icCafeteria,
            label: t('foodie_dining'),
            menuName: 'foodie_dining',
        },
        {
            navigateTo: routes.contractor.companyDocumentation,
            icon: IconsMenuCard.icCompanyDocumentation,
            label: t('company_documentation'),
            menuName: 'companyDocumentation',
        },
        {
            navigateTo: routes.equipmentAndMaterial.list,
            icon: IconsMenuCard.icEquipmentMaterial,
            label: t('equipment_material_control'),
            menuName: 'equipment_material_control',
        }
    ];

    useEffect(() => {
        if (token) {
            const tokeninfo = jwtDecode(token);
            const data = {
                roleId: tokeninfo?.role_id,
            };
            if (data.roleId !== USER_TYPE_NAME_MODEL.NONE) dispatch(RoleCheck(data));
        }
    }, []);

    useEffect(() => {
        const userType = user?.data?.userType?.name;
        if (userType === USER_TYPE_NAME_MODEL.SUPPLIER_IN_CHARGE && user?.data?.status?.id === STATUS_MODEL.TO_APPROVE_DOCUMENT) {
            navigate(routes.suppliers.companyDocumentation);
        } else if (userType === USER_TYPE_NAME_MODEL.CONTRACTOR_IN_CHARGE && user?.data?.status?.id === STATUS_MODEL.TO_APPROVE_DOCUMENT) {
            navigate(routes.contractor.companyDocumentation);
        } else if (userType === USER_TYPE_NAME_MODEL.EMPLOYEE && user?.data?.status?.id === STATUS_MODEL.TO_APPROVE_DOCUMENT) {
            dispatch(GetUserDocumentsEmployee(user?.data?.id));
            navigate(routes.employee.userDocuments);
        }
    }, [user?.data?.id]);

    useEffect(() => {
        const iconBaseGridElements = document.querySelectorAll('.icon-base-grid');
        const imageContainer = document.getElementById('imageContainer');
        let bg;

        // Handle function for mouseover
        const handleMouseOver = (el) => {
            bg = el.getAttribute('data-bg');
            if (imageContainer) {
                imageContainer.style.backgroundImage = bg;
            }
        };

        // Handle function for mouseout
        const handleMouseOut = () => {
            if (imageContainer) {
                imageContainer.style.backgroundImage = bg;
            }
        };

        iconBaseGridElements.forEach((el) => {
            el.addEventListener('mouseover', () => handleMouseOver(el));
            el.addEventListener('mouseout', handleMouseOut);
        });
        if (renderCount < 4) {
            setRenderCount((prevCount) => prevCount + 1);
        }

        // Cleanup function to unmount event listeners
        const cleanup = () => {
            iconBaseGridElements.forEach((el) => {
                el.removeEventListener('mouseover', () => handleMouseOver(el));
                el.removeEventListener('mouseout', handleMouseOut);
            });
        };
        // Clear event listeners when component is unmounted
        return cleanup;
    }, [hover, renderCount]);

    return {
        changeStyle,
        isHovering,
        hover,
        user,
        DEV,
        permission,
        setHover,
        menuItemsEmployee,
        menuItemsSupplier,
        menuItemsContractor
    }
}
